/**
 * 검색어 매칭 공통 유틸.
 *
 * 백엔드 SKU 검색은 구분자를 정규화해서 매칭한다. 프론트가 공백만 제거하면
 * `816083-XHAKB-1000`(입력)과 `816083 XHAKB 1000`(저장값)이 서로 다른 문자열로
 * 취급돼 검색/하이라이트가 어긋난다. 매칭 전 아래 구분자를 모두 제거해 맞춘다.
 */
const SEARCH_SEPARATORS = /[\s\-–—_./\\]+/g;

export function normalizeSearchText(text: string): string {
  return text.replace(SEARCH_SEPARATORS, "").toLowerCase();
}

/**
 * text가 keyword를 포함하는지. 구분자와 대소문자를 무시한다.
 * keyword가 비었거나 구분자뿐이면(검색 조건이 없는 것과 같으므로) true.
 */
export function matchesSearchKeyword(
  text: string | undefined | null,
  keyword: string | undefined | null
): boolean {
  if (!keyword) return true;
  const normalizedKeyword = normalizeSearchText(keyword);
  if (!normalizedKeyword) return true;
  if (!text) return false;
  return normalizeSearchText(text).includes(normalizedKeyword);
}

/**
 * 하이라이트용. 원본 text에서 keywords 중 처음으로 매칭되는 구간을
 * "원본 인덱스"로 돌려준다. 매칭이 없으면 null.
 *
 * 정규화에서 살아남는 문자만 원본 인덱스와 함께 모아두고, 정규화 문자열에서
 * 찾은 위치를 그 맵으로 되돌린다. 그래서 반환 구간은 매칭된 문자 사이에 낀
 * 구분자까지 함께 덮는다 — `816083 XHAKB 1000` 전체가 한 덩어리로 잡힌다.
 */
export function findSearchMatchRange(
  text: string,
  keywords: (string | undefined)[]
): { start: number; end: number } | null {
  if (!text) return null;

  const chars: { char: string; index: number }[] = [];
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    // 한 글자씩 검사하므로 lastIndex가 남지 않도록 test 대신 replace로 판정한다.
    if (char.replace(SEARCH_SEPARATORS, "") !== "") {
      chars.push({ char: char.toLowerCase(), index: i });
    }
  }
  if (chars.length === 0) return null;

  const normalizedText = chars.map((c) => c.char).join("");

  for (const keyword of keywords) {
    if (!keyword) continue;
    const normalizedKeyword = normalizeSearchText(keyword);
    // 구분자만 입력된 검색어. 빈 문자열은 indexOf가 항상 0을 돌려주므로 걸러낸다.
    if (!normalizedKeyword) continue;

    const position = normalizedText.indexOf(normalizedKeyword);
    if (position === -1) continue;

    return {
      start: chars[position].index,
      end: chars[position + normalizedKeyword.length - 1].index + 1,
    };
  }

  return null;
}
